import { provideHttpClient } from '@angular/common/http'
import { NgZone, reflectComponentType, Type, ɵNoopNgZone } from '@angular/core'
import { createApplication } from '@angular/platform-browser'
import { createCustomElement, CustomElementConstructor } from './create-custom-element'

/**
 * A selection of options that configure the custom element created for an Angular component.
 *
 * @public
 */
export interface RegisterCustomElementOptions {
  styleSheets?: Array<CSSStyleSheet>
}

/**
 * A function that when executed, will create and register a custom element for the provided Angular component.
 *
 * @internal
 */
export type RegisterCustomElementFunction<T> = (config?: RegisterCustomElementOptions) => Promise<CustomElementConstructor<T>>

/**
 * Create and register an Angular component as a native custom element.
 *
 * @internal
 *
 * @param component The constructor for the Angular component.
 *
 * @returns         A promise that will resolve with the constructor for the custom element.
 */
export function registerCustomElementFactory<T>(component: Type<T>): RegisterCustomElementFunction<T> {
  return async (configs: RegisterCustomElementOptions = {}) => {
    // Await the application for bootstrapping the Angular components
    let applicationRef = await createApplication({
      providers: [
        { provide: NgZone, useClass: ɵNoopNgZone },
        provideHttpClient()
      ]
    })

    // Create the custom element
    let injector = applicationRef.injector
    let styleSheets = configs?.styleSheets ?? []
    let CustomElement: new (...args) => HTMLElement = createCustomElement<T>(component, { injector, styleSheets })

    // Further extended version of the custom element's class that adds integrations with native HTML forms
    class ExtendedCustomElement extends CustomElement {
      static readonly formAssociated = true
      internals = this.attachInternals()
    }

    // Register the custom element with the custom element registry
    let componentMirror = reflectComponentType(component)

    if (componentMirror) {
      customElements.define(componentMirror.selector, ExtendedCustomElement)
    }

    // Return the constructor for the custom element
    return ExtendedCustomElement as unknown as CustomElementConstructor<T>
  }
}
